import React from 'react';

import { getImportanceLevel, isUnbreakable } from './utils';
import {
  ImportanceValueInputProps,
  UNBREAKABLE_LEVEL_VALUE,
  IMPORTANCE_LEVEL_MAX_VALUES,
  ImportanceLevel,
} from './types';

/**
 * A reusable input component for setting importance values.
 *
 * Note: We need to stop event propagation on all click events to prevent
 * the parent section toggle from being triggered when interacting with
 * the input. This is because the input is rendered inside a button
 * that toggles the section's expanded state.
 */
export default function ImportanceValueInput({
  value,
  onChange,
  className = '',
}: ImportanceValueInputProps) {
  const bracket = getImportanceLevel(value);
  const isUnbreakableValue = isUnbreakable(value);
  return (
    <input
      type={isUnbreakableValue ? 'text' : 'number'}
      min="0"
      step="1"
      className={`w-20 px-2 py-1 border rounded text-center ${bracket.bgColor} ${bracket.color} ${className}`}
      value={isUnbreakableValue ? '∞' : value}
      onClick={e => e.stopPropagation() /* Prevent section toggle */}
      onChange={e => {
        e.stopPropagation(); // Prevent section toggle
        if (isUnbreakableValue) {
          // If the user deleted the infinity symbol, set it back to a numeric value
          if (e.target.value !== '∞') {
            onChange(0);
          } else {
            onChange(UNBREAKABLE_LEVEL_VALUE); // Set to -1 when infinity is shown
          }
        } else {
          const newValue = e.target.value === '' ? 0 : parseInt(e.target.value);
          if (!isNaN(newValue)) {
            // If the value is above the maximum importance level, set it to unbreakable
            onChange(
              newValue > IMPORTANCE_LEVEL_MAX_VALUES[ImportanceLevel.VERY_HIGH]
                ? UNBREAKABLE_LEVEL_VALUE
                : newValue,
            );
          }
        }
      }}
    />
  );
}
